import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout";
import api from "../../api";
import { queuedMessage, getQueuedScores } from "../../offlineQueue";

// Match play never uses each player's/side's full allowance directly — the
// LOWEST allowance in the match plays off scratch, and everyone else
// receives strokes equal to the DIFFERENCE, allocated by stroke index.
// The allowance percentage itself still varies by format (R&A Rules of
// Handicapping, Appendix C):
//   Singles Match Play        100% (i.e. no reduction before the diff)
//   Fourball Match Play        90% of each individual's own handicap
//   Foursomes/Greensomes MP    same combined-side formula as stroke play
//                              (50% combined / 60% low + 40% high), then
//                              the difference is taken between the two
//                              SIDES' combined allowances.
function getMatchType(gameType) {
  // "Better Ball Match Play" is the same fourball mechanic as "Fourball
  // Match Play" (each partner plays their own ball, better one counts)
  // despite the different name — missing here until 2026-09-06, it was
  // silently scored as a singles 1v1 match instead (no Whose-Score toggle,
  // 100% handicap instead of 90%).
  if (gameType === "Fourball Match Play" || gameType === "Better Ball Match Play") return "fourball";
  if (gameType === "Foursomes Match Play" || gameType === "Greensomes Match Play") return "alternate";
  return "singles";
}

export default function MatchPlayScoreEntry() {
  const [event, setEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [selectedPair, setSelectedPair] = useState(null);
  // One gross score per side per hole — for fourball it's credited to
  // whichever of that side's 2 players had the better ball (see
  // holeScorerA/B), for singles/alternate there's only ever one contributor
  // per side so no toggle is needed.
  const [holeGrossA, setHoleGrossA] = useState({});
  const [holeGrossB, setHoleGrossB] = useState({});
  const [holeScorerA, setHoleScorerA] = useState({});
  const [holeScorerB, setHoleScorerB] = useState({});
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const toastTimerRef = React.useRef(null);

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { if (selectedPair) loadPairScores(selectedPair); }, [selectedPair?.display]);

  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(""), 5000);
  };

  const matchType = getMatchType(event?.game_type || "");

  const loadAll = async () => {
    try {
      const eventRes = await api.get("/events/current");
      if (!eventRes.data) { setLoading(false); return; }
      setEvent(eventRes.data);

      if (eventRes.data.course_id) {
        const holesRes = await api.get("/courses/" + eventRes.data.course_id + "/holes");
        setHoles(holesRes.data);
      }

      const groupRes = await api.get("/groups");
      const players = (groupRes.data.players || []).filter(p => p.playing);

      const grpMap = {};
      players.forEach(p => {
        if (!grpMap[p.grp]) grpMap[p.grp] = {};
        const tid = p.team_id || 0;
        if (!grpMap[p.grp][tid]) grpMap[p.grp][tid] = [];
        grpMap[p.grp][tid].push(p);
      });

      const pairsArr = [];
      Object.entries(grpMap).sort((a,b) => parseInt(a[0])-parseInt(b[0])).forEach(([grp, teamGroups]) => {
        const teamIds = Object.keys(teamGroups);
        if (teamIds.length >= 2) {
          const sideA = teamGroups[teamIds[0]];
          const sideB = teamGroups[teamIds[1]];
          const nameA = sideA.map(p => p.name.split(" ")[0]).join("/");
          const nameB = sideB.map(p => p.name.split(" ")[0]).join("/");
          pairsArr.push({
            grp: parseInt(grp),
            display: `Group ${grp}: ${nameA} vs ${nameB}`,
            nameA, nameB, sideA, sideB,
          });
        }
      });

      setPairs(pairsArr);
      if (pairsArr.length > 0) setSelectedPair(pairsArr[0]);
      else setLoading(false);
    } catch(e) { console.error(e); setLoading(false); }
  };

  const loadPairScores = async (pair) => {
    if (!event) return;
    setLoading(true);
    try {
      const scoresRes = await api.get("/scores/" + event.id);
      // Overlay any score already typed and queued (but not yet applied
      // server-side, e.g. entered while offline) so navigating away and
      // back never looks like it was lost.
      const pending = getQueuedScores(event.id);
      const combinedScores = scoresRes.data.slice();
      Object.entries(pending).forEach(([pid, holeScores]) => {
        Object.entries(holeScores).forEach(([hole, gross]) => {
          const pidNum = parseInt(pid), holeNum = parseInt(hole);
          const idx = combinedScores.findIndex(s => s.player_id === pidNum && s.hole === holeNum);
          if (idx >= 0) combinedScores[idx] = { ...combinedScores[idx], gross };
          else combinedScores.push({ player_id: pidNum, hole: holeNum, gross });
        });
      });
      const grossA = {}, grossB = {}, scorerA = {}, scorerB = {};
      holes.forEach(h => {
        const findFor = (side) => side.map((p, idx) => ({
          idx, s: combinedScores.find(s => s.player_id === p.player_id && s.hole === h.hole && s.gross)
        })).find(x => x.s);
        const foundA = findFor(pair.sideA);
        const foundB = findFor(pair.sideB);
        if (foundA) { grossA[h.hole] = foundA.s.gross; scorerA[h.hole] = foundA.idx === 0 ? "p1" : "p2"; }
        else scorerA[h.hole] = "p1";
        if (foundB) { grossB[h.hole] = foundB.s.gross; scorerB[h.hole] = foundB.idx === 0 ? "p1" : "p2"; }
        else scorerB[h.hole] = "p1";
      });
      setHoleGrossA(grossA); setHoleGrossB(grossB);
      setHoleScorerA(scorerA); setHoleScorerB(scorerB);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ---- Allowance calculation, per the format table above ----
  const sideAllowance = (side) => {
    if (matchType === "alternate") {
      const combined = side.reduce((s, p) => s + ((p.hcp_adj ?? p.handicap) || 0), 0);
      if (event?.game_type === "Greensomes Match Play") {
        const lower = Math.min(...side.map(p => (p.hcp_adj ?? p.handicap) || 0));
        const higher = Math.max(...side.map(p => (p.hcp_adj ?? p.handicap) || 0));
        return Math.round(0.6 * lower + 0.4 * higher);
      }
      return Math.round(combined * 0.5); // Foursomes Match Play
    }
    // singles: 100% (raw Course HCP); fourball: 90% each — handled per-player
    // below, this function is only used for 'alternate' sides as one number.
    return (side[0]?.hcp_adj ?? side[0]?.handicap) || 0;
  };

  const playerAllowance = (player) => {
    const raw = (player?.hcp_adj ?? player?.handicap) || 0;
    return matchType === "fourball" ? Math.round(raw * 0.9) : raw; // singles = 100%
  };

  // The lowest allowance anywhere in this specific match plays off scratch.
  const getLowBaseline = () => {
    if (!selectedPair) return 0;
    if (matchType === "alternate") {
      return Math.min(sideAllowance(selectedPair.sideA), sideAllowance(selectedPair.sideB));
    }
    if (matchType === "fourball") {
      const all = [...selectedPair.sideA, ...selectedPair.sideB].map(playerAllowance);
      return Math.min(...all);
    }
    return Math.min(playerAllowance(selectedPair.sideA[0]), playerAllowance(selectedPair.sideB[0]));
  };

  const strokesFor = (allowance, hole, lowBaseline) => {
    const diff = Math.max(0, allowance - lowBaseline);
    const base = Math.floor(diff / 18);
    const extra = diff % 18;
    return base + (hole.stroke_index <= extra ? 1 : 0);
  };

  const getSideNet = (side, gross, scorerKey, hole, lowBaseline) => {
    if (!gross) return null;
    let allowance;
    if (matchType === "alternate") allowance = sideAllowance(side);
    else if (matchType === "fourball") allowance = playerAllowance(scorerKey === "p1" ? side[0] : side[1]);
    else allowance = playerAllowance(side[0]);
    return parseInt(gross) - strokesFor(allowance, hole, lowBaseline);
  };

  const getHoleResult = (hole) => {
    if (!selectedPair) return null;
    const lowBaseline = getLowBaseline();
    const netA = getSideNet(selectedPair.sideA, holeGrossA[hole], holeScorerA[hole] || "p1", hole, lowBaseline);
    const netB = getSideNet(selectedPair.sideB, holeGrossB[hole], holeScorerB[hole] || "p1", hole, lowBaseline);
    if (netA === null || netB === null) return { result: null, netA, netB };
    if (netA < netB) return { result: "A", netA, netB };
    if (netB < netA) return { result: "B", netA, netB };
    return { result: "H", netA, netB };
  };

  const holeResults = holes.map(h => getHoleResult(h.hole));

  const getMatchClosed = () => {
    for (let i = 0; i < holes.length; i++) {
      const results = holeResults.slice(0, i + 1).map(r => r?.result);
      let score = 0;
      for (const r of results) { if (r === "A") score++; else if (r === "B") score--; }
      const holesLeft = holes.length - (i + 1);
      if (Math.abs(score) > holesLeft) return i + 1;
    }
    return null;
  };

  const getMatchStatus = (upToHole) => {
    const closedAtHole = getMatchClosed();
    if (closedAtHole && upToHole > closedAtHole) return "Match Closed";
    const results = holeResults.slice(0, upToHole).map(r => r?.result);
    let score = 0;
    for (const r of results) { if (r === "A") score++; else if (r === "B") score--; }
    const holesLeft = holes.length - upToHole;
    if (score === 0) return "AS";
    const lead = Math.abs(score);
    const leader = score > 0 ? (selectedPair?.nameA || "A") : (selectedPair?.nameB || "B");
    if (lead > holesLeft) return `${leader} wins ${lead}&${holesLeft}`;
    return `${lead}UP`;
  };

  const handleScorerToggle = (side, hole, which) => {
    if (side === "A") setHoleScorerA(prev => ({ ...prev, [hole]: which }));
    else setHoleScorerB(prev => ({ ...prev, [hole]: which }));
  };

  const buildSaveScores = () => {
    const allScores = [];
    holes.forEach(h => {
      const gA = parseInt(holeGrossA[h.hole]);
      const gB = parseInt(holeGrossB[h.hole]);
      const scorerAKey = holeScorerA[h.hole] || "p1";
      const scorerBKey = holeScorerB[h.hole] || "p1";
      selectedPair.sideA.forEach((p, idx) => {
        const key = idx === 0 ? "p1" : "p2";
        const credited = matchType === "fourball" ? scorerAKey === key : idx === 0; // singles/alternate: lead player only
        allScores.push({ player_id: p.player_id, hole: h.hole, gross: (gA && credited) ? gA : 0, side: "A" });
      });
      selectedPair.sideB.forEach((p, idx) => {
        const key = idx === 0 ? "p1" : "p2";
        const credited = matchType === "fourball" ? scorerBKey === key : idx === 0;
        allScores.push({ player_id: p.player_id, hole: h.hole, gross: (gB && credited) ? gB : 0, side: "B" });
      });
    });
    return allScores;
  };

  const saveScores = async () => {
    if (!event || !selectedPair) return;
    try {
      const res = await api.post("/scores/save", { event_id: event.id, scores: buildSaveScores(), publish: false });
      showToast(queuedMessage(res, "Scores saved!"));
    } catch(e) { showToast("Failed: " + e.message); }
  };

  const randomScores = () => {
    if (!selectedPair) return;
    const gA = {}, gB = {}, sA = {}, sB = {};
    holes.forEach(h => {
      gA[h.hole] = h.par + Math.floor(Math.random() * 3) - 1;
      gB[h.hole] = h.par + Math.floor(Math.random() * 3) - 1;
      sA[h.hole] = Math.random() < 0.5 ? "p1" : "p2";
      sB[h.hole] = Math.random() < 0.5 ? "p1" : "p2";
    });
    setHoleGrossA(gA); setHoleGrossB(gB); setHoleScorerA(sA); setHoleScorerB(sB);
    showToast("Random scores generated");
  };

  const isClosedAt = (holeNum) => {
    const closedAtHole = getMatchClosed();
    return closedAtHole && holeNum > closedAtHole;
  };

  return (
    <Layout>
      {toast && (
        <div style={{position:"fixed",top:"70px",left:"50%",transform:"translateX(-50%)",zIndex:9999,minWidth:"280px",textAlign:"center"}} className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-2xl text-base font-bold border-2 border-white">
          ✓ {toast}
        </div>
      )}
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-2 flex items-center gap-4">
          <span className="font-semibold text-sm">{event?.game_type || "Match Play"} Score Entry</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={saveScores} className="bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Save Scores</button>
            <button type="button" onClick={randomScores} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold px-3 py-1.5 rounded">Random Scores</button>
          </div>
        </div>

        {selectedPair && (() => {
          // The number that actually determines strokes given is never the
          // raw allowance itself — match play plays off the LOWEST allowance
          // anywhere in the match (that player/side plays scratch), everyone
          // else gets the DIFFERENCE. Showing the raw 90%/50%/60-40 number
          // without subtracting the baseline reads as if that's how many
          // strokes someone gets, which is wrong for everyone except whoever
          // has the single lowest allowance — flagged live 2026-09-06.
          const lowBaseline = getLowBaseline();
          const strokesFor = (allowance) => Math.max(0, allowance - lowBaseline);
          return (
            <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 text-xs text-blue-900 flex flex-wrap gap-x-6 gap-y-1">
              {matchType === "fourball" && (
                <>
                  <span style={{ fontWeight: 700 }}>R&amp;A Rules of Handicapping — Fourball: 90% of Course Handicap = Playing Handicap; lowest Playing Handicap plays off scratch</span>
                  {[...selectedPair.sideA, ...selectedPair.sideB].map(p => {
                    const ph = playerAllowance(p);
                    const strokes = strokesFor(ph);
                    return (
                      <span key={p.player_id}>
                        <strong>{p.name.split(" ")[0]}:</strong> Course HCP {p.hcp_adj ?? p.handicap} × 90% = <strong>Play HCP {ph}</strong>
                        {strokes === 0 ? <span style={{ color: "#166534", fontWeight: 700 }}> (scratch)</span> : <> → <strong>{strokes} strokes</strong></>}
                      </span>
                    );
                  })}
                </>
              )}
              {matchType === "alternate" && (
                <>
                  <span style={{ fontWeight: 700 }}>
                    R&amp;A Rules of Handicapping — {event?.game_type === "Greensomes Match Play" ? "Greensomes: 60% of lower hcp + 40% of higher = side Playing Handicap" : "Foursomes: 50% of combined handicap = side Playing Handicap"}; lower plays off scratch
                  </span>
                  {[[selectedPair.nameA, selectedPair.sideA], [selectedPair.nameB, selectedPair.sideB]].map(([name, side]) => {
                    const ph = sideAllowance(side);
                    const strokes = strokesFor(ph);
                    return (
                      <span key={name}>
                        <strong>{name}:</strong> <strong>Play HCP {ph}</strong>
                        {strokes === 0 ? <span style={{ color: "#166534", fontWeight: 700 }}> (scratch)</span> : <> → <strong>{strokes} strokes</strong></>}
                      </span>
                    );
                  })}
                </>
              )}
              {matchType === "singles" && (
                <>
                  <span style={{ fontWeight: 700 }}>R&amp;A Rules of Handicapping — Team Match Play: 100% of Course Handicap = Playing Handicap; lowest plays off scratch</span>
                  <span><strong>{selectedPair.nameA}:</strong> Course HCP {selectedPair.sideA[0]?.hcp_adj ?? selectedPair.sideA[0]?.handicap} → {strokesFor(playerAllowance(selectedPair.sideA[0]))} strokes</span>
                  <span><strong>{selectedPair.nameB}:</strong> Course HCP {selectedPair.sideB[0]?.hcp_adj ?? selectedPair.sideB[0]?.handicap} → {strokesFor(playerAllowance(selectedPair.sideB[0]))} strokes</span>
                </>
              )}
            </div>
          );
        })()}

        <div className="flex flex-1 overflow-hidden">
          <div className="w-56 flex-shrink-0 border-r border-gray-300 bg-yellow-50 overflow-y-auto">
            <div className="px-3 py-2 text-sm font-extrabold text-green-900 uppercase tracking-wide sticky top-0 bg-yellow-200 border-b-2 border-green-800">Every Match Playing</div>
            <div className="flex flex-col">
              {pairs.map((p, i) => (
                <button key={p.display} type="button" onClick={() => setSelectedPair(p)}
                  className={"text-left text-xs font-semibold px-3 py-2 border-b border-yellow-100 " +
                    (selectedPair?.display === p.display
                      ? "bg-blue-600 text-white"
                      : (i % 2 === 0 ? "bg-white" : "bg-yellow-100") + " text-gray-700 hover:bg-blue-50")}>
                  {p.display}
                </button>
              ))}
              {pairs.length === 0 && <div className="px-3 py-4 text-xs text-gray-500">No matches found.</div>}
            </div>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden">
            {loading && <div className="bg-blue-50 text-blue-700 px-4 py-2 text-sm">Loading...</div>}
            <div className="flex-1 overflow-auto">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-green-800 text-white">
                  <tr>
                    <th className="px-3 py-2 text-left w-12">Hole</th>
                    <th className="px-3 py-2 text-center w-12">Par</th>
                    <th className="px-3 py-2 text-center w-12">SI</th>
                    <th className="px-3 py-2 text-center w-32">{selectedPair?.nameA || "Side A"}</th>
                    {matchType === "fourball" && <th className="px-3 py-2 text-center w-32">Whose ({selectedPair?.nameA})</th>}
                    <th className="px-3 py-2 text-center w-32">{selectedPair?.nameB || "Side B"}</th>
                    {matchType === "fourball" && <th className="px-3 py-2 text-center w-32">Whose ({selectedPair?.nameB})</th>}
                    <th className="px-3 py-2 text-center w-16">Net A</th>
                    <th className="px-3 py-2 text-center w-16">Net B</th>
                    <th className="px-3 py-2 text-left w-28">Winner</th>
                    <th className="px-3 py-2 text-left w-24">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {holes.map((hole, i) => {
                    const { result, netA, netB } = getHoleResult(hole.hole) || {};
                    const matchStatus = getMatchStatus(i + 1);
                    const closed = isClosedAt(hole.hole);
                    const winner = closed ? "" : result === "A" ? selectedPair?.nameA : result === "B" ? selectedPair?.nameB : result === "H" ? "Halved" : "";
                    const scorerAKey = holeScorerA[hole.hole] || "p1";
                    const scorerBKey = holeScorerB[hole.hole] || "p1";
                    return (
                      <tr key={hole.hole} className={"border-b border-gray-200 " + (closed ? "bg-gray-100 opacity-50" : i%2===0 ? "bg-white" : "bg-green-50")}>
                        <td className="px-3 py-1.5 font-medium">{hole.hole}</td>
                        <td className="px-3 py-1.5 text-center">{hole.par}</td>
                        <td className="px-3 py-1.5 text-center text-gray-500">{hole.stroke_index}</td>
                        <td className="px-3 py-1.5 text-center">
                          <input type="number" min="1" max="15" disabled={closed}
                            value={holeGrossA[hole.hole] || ""}
                            onChange={e => setHoleGrossA(prev => ({ ...prev, [hole.hole]: e.target.value }))}
                            className="border border-gray-300 rounded px-1 py-0.5 w-16 text-xs text-center focus:border-blue-500 focus:outline-none"
                          />
                        </td>
                        {matchType === "fourball" && (
                          <td className="px-3 py-1.5 text-center">
                            <div className="inline-flex rounded overflow-hidden border border-gray-300 text-[11px]">
                              <button type="button" onClick={() => handleScorerToggle("A", hole.hole, "p1")}
                                className={"px-2 py-1 font-semibold " + (scorerAKey === "p1" ? "bg-blue-600 text-white" : "bg-white text-gray-600")}>
                                {selectedPair?.sideA?.[0]?.name?.split(" ")[0] || "P1"}
                              </button>
                              <button type="button" onClick={() => handleScorerToggle("A", hole.hole, "p2")}
                                className={"px-2 py-1 font-semibold " + (scorerAKey === "p2" ? "bg-blue-600 text-white" : "bg-white text-gray-600")}>
                                {selectedPair?.sideA?.[1]?.name?.split(" ")[0] || "P2"}
                              </button>
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-1.5 text-center">
                          <input type="number" min="1" max="15" disabled={closed}
                            value={holeGrossB[hole.hole] || ""}
                            onChange={e => setHoleGrossB(prev => ({ ...prev, [hole.hole]: e.target.value }))}
                            className="border border-gray-300 rounded px-1 py-0.5 w-16 text-xs text-center focus:border-blue-500 focus:outline-none"
                          />
                        </td>
                        {matchType === "fourball" && (
                          <td className="px-3 py-1.5 text-center">
                            <div className="inline-flex rounded overflow-hidden border border-gray-300 text-[11px]">
                              <button type="button" onClick={() => handleScorerToggle("B", hole.hole, "p1")}
                                className={"px-2 py-1 font-semibold " + (scorerBKey === "p1" ? "bg-blue-600 text-white" : "bg-white text-gray-600")}>
                                {selectedPair?.sideB?.[0]?.name?.split(" ")[0] || "P1"}
                              </button>
                              <button type="button" onClick={() => handleScorerToggle("B", hole.hole, "p2")}
                                className={"px-2 py-1 font-semibold " + (scorerBKey === "p2" ? "bg-blue-600 text-white" : "bg-white text-gray-600")}>
                                {selectedPair?.sideB?.[1]?.name?.split(" ")[0] || "P2"}
                              </button>
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-1.5 text-center">{netA ?? ""}</td>
                        <td className="px-3 py-1.5 text-center">{netB ?? ""}</td>
                        <td className="px-3 py-1.5 text-xs font-medium text-blue-700">{winner}</td>
                        <td className="px-3 py-1.5 text-xs font-bold">{matchStatus}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!loading && holes.length === 0 && (
                <div className="text-center py-8 text-gray-500">No course loaded. Go to Course page and select a course.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
